import { Badge, Box, Spinner, Text, VStack } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import type { CaseMessage } from '@/lib/services/caseThreadsService'
import { getThreadMessageBody } from '@/lib/services/caseThreadsService'
import { sanitizeEmailHtml } from '@/lib/utils/emailSafety'

const directionLabel: Record<CaseMessage['direction'], string> = {
  inbound: 'Customer',
  outbound: 'Agent',
  internal_note: 'Internal note',
  system: 'System',
}

function formatWhen(value: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

function htmlHasText(html: string): boolean {
  const text = html
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .trim()
  return text.length > 0
}

function normalizeEmailHtml(html: string): string {
  const source = sanitizeEmailHtml(html).trim()
  if (!source) return ''
  let normalized = source.replace(/(src|href)=["']\/\/([^"']+)["']/gi, '$1="https://$2"')
  const responsiveStyles =
    '<style>img{max-width:100%;height:auto;}table{max-width:100%;}body{margin:0;padding:8px;box-sizing:border-box;font-family:Segoe UI,Arial,sans-serif;font-size:14px;color:#1a202c;}</style>'
  if (/<head[^>]*>/i.test(normalized)) {
    normalized = normalized.replace(/<head[^>]*>/i, (match) => `${match}${responsiveStyles}`)
  } else {
    normalized = `<html><head>${responsiveStyles}</head><body>${normalized}</body></html>`
  }
  return normalized
}

export function MessageBubble({ message }: { message: CaseMessage }) {
  const isResponse = message.direction === 'outbound' || message.direction === 'internal_note'
  const isNote = message.direction === 'internal_note' || message.direction === 'system'
  const body = useQuery({
    queryKey: ['case-threads', 'message-body', message.id],
    queryFn: () => getThreadMessageBody(message.id),
    enabled: !isNote,
    staleTime: 5 * 60 * 1000,
  })
  const html = body.data && htmlHasText(body.data) ? normalizeEmailHtml(body.data) : ''
  const when = formatWhen(message.received_at || message.sent_at || message.created_at)
  const sender = [message.from_name, message.from_email].filter((part) => part && part.trim() !== '').join(' · ')

  return (
    <Box
      w={isResponse ? 'calc(100% - 50px)' : '100%'}
      ml={isResponse ? '50px' : 0}
      p={3}
      borderWidth="1px"
      borderRadius="md"
      bg={message.direction === 'internal_note' ? 'yellow.50' : 'white'}
    >
      <Text fontSize="xs" color="gray.500">
        {directionLabel[message.direction]}
        {sender ? ` · ${sender}` : ''}
        {when ? ` · ${when}` : ''}
      </Text>
      {message.to?.length ? (
        <Text fontSize="xs" color="gray.500">
          To: {message.to.join(', ')}
        </Text>
      ) : null}
      {message.subject ? (
        <Text fontSize="sm" fontWeight="semibold">
          {message.subject}
        </Text>
      ) : null}
      {isNote || !html ? (
        <Text fontSize="sm" whiteSpace="pre-wrap">
          {message.body_preview || '(No preview stored)'}
          {body.isFetching && !isNote ? ' ' : ''}
        </Text>
      ) : (
        <Box mt={2} borderWidth="1px" borderRadius="md" overflow="hidden" bg="white">
          <iframe
            title={message.subject || 'Email body'}
            sandbox="allow-same-origin"
            srcDoc={html}
            style={{ width: '100%', minHeight: '160px', border: 'none', background: 'white' }}
            onLoad={(event) => {
              const frame = event.currentTarget
              const height = frame.contentDocument?.documentElement?.scrollHeight
              if (height) frame.style.height = `${Math.max(height, 120)}px`
            }}
          />
        </Box>
      )}
      {body.isFetching && !html && !isNote ? <Spinner mt={2} size="xs" /> : null}
      {message.cc?.length ? (
        <Text fontSize="xs" color="gray.500">
          Cc: {message.cc.join(', ')}
        </Text>
      ) : null}
      {message.attachments?.length ? (
        <VStack align="stretch" mt={2} spacing={1}>
          {message.attachments.map((file) => (
            <Badge key={file.id} variant="outline">
              {file.filename}
              {file.size ? ` (${file.size} bytes)` : ''}
            </Badge>
          ))}
        </VStack>
      ) : null}
    </Box>
  )
}
